import React, { useState, useEffect } from 'react';
import { Vacancy, PremiumJobboardsData } from '../types';
import { Linkedin, Mail, CheckCircle, Search, Monitor, ExternalLink, Calendar, Calculator, Send, Eye, Sparkles, ChevronRight, Clock } from 'lucide-react';

interface VacancyPremiumJobboardsProps {
    vacancy: Vacancy;
    onUpdate: (updates: Partial<PremiumJobboardsData>) => Promise<void> | void;
    isReadOnly?: boolean;
    onNext?: () => void;
}

const PRICING = {
    linkedin: { monthly: 295 },
    indeed: { sponsored: 25, premium: 50 },
    werkzoeken: { sponsored: 25 },
    ictergezocht: { basis: 325, premium: 425 }
};

const VacancyPremiumJobboards: React.FC<VacancyPremiumJobboardsProps> = ({ vacancy, onUpdate, isReadOnly, onNext }) => {
    const [data, setData] = useState<PremiumJobboardsData>(vacancy.data?.premiumJobboards || {});
    const [previewBoard, setPreviewBoard] = useState<'linkedin' | 'indeed' | 'werkzoeken' | 'ictergezocht' | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [savedOk, setSavedOk] = useState(false);

    const isIT = vacancy.client?.industry?.toLowerCase().includes('it') || vacancy.title?.toLowerCase().includes('it') || vacancy.title?.toLowerCase().includes('developer');

    useEffect(() => {
        setData(vacancy.data?.premiumJobboards || {});
    }, [vacancy.data?.premiumJobboards]);

    const handleChange = (board: keyof PremiumJobboardsData, updates: any) => {
        if (isReadOnly) return;
        const newData = { ...data, [board]: { ...data[board], ...updates } };
        setData(newData);
        onUpdate({ premiumJobboards: newData } as any); // Using the prop slightly differently: we pass the whole object wrapped since we merge it in parent
    };

    const toggleBoard = (board: keyof PremiumJobboardsData) => {
        if (isReadOnly) return;
        setPreviewBoard(board);
        if (data[board]?.selected) {
            const newData = { ...data };
            delete newData[board];
            setData(newData);
            onUpdate({ premiumJobboards: newData } as any);
            return;
        }

        let defaults: any = { selected: true };
        if (board === 'indeed') defaults = { ...defaults, tier: 'sponsored', endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] };
        if (board === 'werkzoeken') defaults = { ...defaults, endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] };
        if (board === 'ictergezocht') defaults = { ...defaults, tier: 'basis' };

        const newData = { ...data, [board]: defaults };
        setData(newData);
        onUpdate({ premiumJobboards: newData } as any);
    };

    const calculateDays = (endDateStr?: string) => {
        if (!endDateStr) return 0;
        const end = new Date(endDateStr);
        const start = new Date();
        const diffDiff = end.getTime() - start.getTime();
        const days = Math.ceil(diffDiff / (1000 * 3600 * 24));
        return Math.max(0, days);
    };

    const calculateTotal = () => {
        let total = 0;
        if (data.linkedin?.selected) total += PRICING.linkedin.monthly;
        if (data.indeed?.selected) {
            const days = calculateDays(data.indeed.endDate);
            total += (data.indeed.tier === 'premium' ? PRICING.indeed.premium : PRICING.indeed.sponsored) * days;
        }
        if (data.werkzoeken?.selected) {
            const days = calculateDays(data.werkzoeken.endDate);
            total += PRICING.werkzoeken.sponsored * days;
        }
        if (data.ictergezocht?.selected) {
            total += data.ictergezocht.tier === 'premium' ? PRICING.ictergezocht.premium : PRICING.ictergezocht.basis;
        }
        return total;
    };

        const handleSendRequest = async () => {
        setIsSaving(true);
        try {
            // Set pending status for selected boards
            const newData = { ...data };
            if (newData.linkedin?.selected && newData.linkedin.status !== 'Online') newData.linkedin.status = 'Aangevraagd';
            if (newData.indeed?.selected && newData.indeed.status !== 'Online') newData.indeed.status = 'Aangevraagd';
            if (newData.werkzoeken?.selected && newData.werkzoeken.status !== 'Online') newData.werkzoeken.status = 'Aangevraagd';
            if (newData.ictergezocht?.selected && newData.ictergezocht.status !== 'Online') newData.ictergezocht.status = 'Aangevraagd';
            
            setData(newData);

            // First save state
            await onUpdate({ premiumJobboards: newData } as any);

            const apiUrl = window.location.hostname !== 'localhost' ? '' : 'http://localhost:4000';
            
            const res = await fetch(`${apiUrl}/api/premium-channels/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    vacancyId: vacancy.id,
                    vacancyTitle: vacancy.title,
                    clientName: vacancy.client?.name || 'Onbekende Klant',
                    totalCost: calculateTotal(),
                    data: newData
                })
            });
            
            if (!res.ok) throw new Error('Failed to send request');
            
            setSavedOk(true);
            setTimeout(() => setSavedOk(false), 3000);
        } catch(e) {
            console.error(e);
            alert('Versturen aanvraag mislukt.');
        } finally {
            setIsSaving(false);
        }
    };

    const generateEmailContent = () => {
        const lines = [
            `Beste Werfplaats team,`,
            ``,
            `Graag wil ik de volgende premium jobboards activeren voor vacature: ${vacancy.title} (${vacancy.client?.name || 'Onbekende Klant'}).`,
            ``,
            `Geselecteerde opties:`,
        ];

        if (data.linkedin?.selected) lines.push(`- LinkedIn Jobslot (€295 / maand)`);
        if (data.indeed?.selected) {
            lines.push(`- Indeed Campagne (${data.indeed.tier === 'premium' ? 'Premium €50/dag' : 'Sponsored €25/dag'}) tot ${data.indeed.endDate || 'Onbekend'}`);
        }
        if (data.werkzoeken?.selected) {
            lines.push(`- Werkzoeken.nl (Sponsored €25/dag) tot ${data.werkzoeken.endDate || 'Onbekend'}`);
        }
        if (data.ictergezocht?.selected) {
            lines.push(`- ICT'er gezocht (${data.ictergezocht.tier === 'premium' ? 'Premium €425' : 'Basis €325'} - Looptijd 6 maanden)`);
        }

        lines.push(``);
        lines.push(`Geschatte kosten: €${calculateTotal().toLocaleString('nl-NL')}`);
        lines.push(``);
        lines.push(`Vacature link: ${window.location.origin}/vacancy/${vacancy.id}`);
        lines.push(`Alvast bedankt!`);

        return encodeURIComponent(lines.join('\n'));
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Top action bar */}
            <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div>
                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Premium Jobboards</h3>
                    <p className="text-xs text-slate-500 font-medium">Selecteer kanalen of ga direct verder.</p>
                </div>
                <div className="flex items-center gap-2">
                    {!isReadOnly && (
                        <button
                            onClick={async () => {
                                setIsSaving(true);
                                setSavedOk(false);
                                try {
                                    await onUpdate({ premiumJobboards: data } as any);
                                    setSavedOk(true);
                                    setTimeout(() => setSavedOk(false), 2000);
                                } finally {
                                    setIsSaving(false);
                                }
                            }}
                            disabled={isSaving}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg font-black text-[10px] uppercase tracking-widest border transition-all active:scale-95 disabled:opacity-50 bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                        >
                            {isSaving ? (
                                <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"/><path fill="currentColor" d="M4 12a8 8 0 018-8v8z" className="opacity-75"/></svg>
                            ) : savedOk ? (
                                <CheckCircle size={14} className="text-emerald-500" />
                            ) : (
                                <CheckCircle size={14} />
                            )}
                            {savedOk ? 'Opgeslagen!' : 'Opslaan'}
                        </button>
                    )}
                    {onNext && (
                        <button
                            onClick={onNext}
                            className="bg-[#e51b23] text-white px-5 py-2 rounded-lg font-black text-[10px] uppercase tracking-widest shadow-md hover:bg-[#B71C1C] transition-all flex items-center gap-2 active:scale-95"
                        >
                            Volgende <ChevronRight size={14} />
                        </button>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Configuration Column */}
                <div className="lg:col-span-6 space-y-6">
                    <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
                        <div className="flex items-center gap-4 mb-3">
                            <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-500 shadow-inner">
                                <Monitor size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Kies Premium Kanalen</h3>
                                <p className="text-sm text-slate-500 font-medium">Vergroot je bereik substantieel</p>
                            </div>
                        </div>
                        <p className="text-xs text-slate-500 mb-6 leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
                            Kies welke premium kanalen je wilt inzetten om je vacature extra onder de aandacht te brengen.
                            <strong> LinkedIn Jobslot</strong> is ideaal voor het werven van professionals via het grootste zakelijke netwerk.
                            <strong> Indeed</strong> en <strong>Werkzoeken.nl</strong> bieden gesponsorde campagnes aan voor maximale zichtbaarheid bij actieve baanzoekers.
                            Zodra je de aanvraag verstuurt, zet een Werfplaats recruiter de campagnes direct voor je live.
                        </p>

                        <div className="space-y-4">
                            {/* LinkedIn */}
                            <div className={`p-4 rounded-xl border-2 transition-all cursor-pointer flex flex-col gap-4 ${data.linkedin?.selected ? 'border-[#0077b5] bg-[#0077b5]/5' : 'border-slate-100 hover:border-slate-300'}`} onClick={() => toggleBoard('linkedin')}>
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${data.linkedin?.selected ? 'border-[#0077b5] bg-[#0077b5]' : 'border-slate-300'}`}>
                                            {data.linkedin?.selected && <CheckCircle size={14} className="text-white" />}
                                        </div>
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png" alt="LinkedIn" className="w-6 h-6 object-contain" />
                                        <span className="font-bold text-slate-900 flex items-center gap-2">LinkedIn Jobslot 
            {data.linkedin?.status === 'Online' && <span className="text-[9px] font-black bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full uppercase">Live</span>}
            {data.linkedin?.status === 'Aangevraagd' && <span className="text-[9px] font-black bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase flex items-center gap-1"><Clock size={10}/> Wachtend op activatie</span>}
            {data.linkedin?.activeLink && <a href={data.linkedin.activeLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-blue-500 hover:text-blue-700"><ExternalLink size={12} /></a>}
        </span>
                                    </div>
                                    <span className="font-bold text-slate-500">€{PRICING.linkedin.monthly} /mnd</span>
                                </div>
                            </div>

                            {/* Indeed */}
                            <div className={`p-4 rounded-xl border-2 transition-all flex flex-col gap-4 ${data.indeed?.selected ? 'border-[#003A9B] bg-[#003A9B]/5' : 'border-slate-100'}`}>
                                <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleBoard('indeed')}>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${data.indeed?.selected ? 'border-[#003A9B] bg-[#003A9B]' : 'border-slate-300'}`}>
                                            {data.indeed?.selected && <CheckCircle size={14} className="text-white" />}
                                        </div>
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Indeed_logo.png" alt="Indeed" className="w-auto h-5 object-contain" />
                                        <span className="font-bold text-slate-900 flex items-center gap-2">Indeed Campagne 
            {data.indeed?.status === 'Online' && <span className="text-[9px] font-black bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full uppercase">Live</span>}
            {data.indeed?.status === 'Aangevraagd' && <span className="text-[9px] font-black bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase flex items-center gap-1"><Clock size={10}/> Wachtend op activatie</span>}
            {data.indeed?.activeLink && <a href={data.indeed.activeLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-blue-500 hover:text-blue-700"><ExternalLink size={12} /></a>}
        </span>
                                    </div>
                                    <span className="font-bold text-slate-500">Vanaf €{PRICING.indeed.sponsored} /dag</span>
                                </div>
                                {data.indeed?.selected && (
                                    <div className="pl-9 space-y-3 pt-2 border-t border-slate-200 mt-2" onClick={e => e.stopPropagation()}>
                                        <div className="flex gap-2">
                                            <button onClick={() => handleChange('indeed', { tier: 'sponsored' })} className={`flex-1 py-1.5 rounded-md text-xs font-bold border ${data.indeed.tier === 'sponsored' ? 'bg-[#003A9B] text-white border-[#003A9B]' : 'bg-white text-slate-600 border-slate-200'}`}>Sponsored (€25/dag)</button>
                                            <button onClick={() => handleChange('indeed', { tier: 'premium' })} className={`flex-1 py-1.5 rounded-md text-xs font-bold border ${data.indeed.tier === 'premium' ? 'bg-[#003A9B] text-white border-[#003A9B]' : 'bg-white text-slate-600 border-slate-200'}`}>Premium (€50/dag)</button>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-medium text-slate-600">Looptijd tot:</span>
                                            <input type="date" value={data.indeed.endDate || ''} onChange={(e) => handleChange('indeed', { endDate: e.target.value })} className="border border-slate-200 rounded-md px-2 py-1 text-xs" />
                                            <span className="text-xs text-slate-400 font-bold ml-auto">{calculateDays(data.indeed.endDate)} dagen</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Werkzoeken.nl */}
                            <div className={`p-4 rounded-xl border-2 transition-all flex flex-col gap-4 ${data.werkzoeken?.selected ? 'border-[#ff5a5f] bg-[#ff5a5f]/5' : 'border-slate-100'}`}>
                                <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleBoard('werkzoeken')}>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${data.werkzoeken?.selected ? 'border-[#ff5a5f] bg-[#ff5a5f]' : 'border-slate-300'}`}>
                                            {data.werkzoeken?.selected && <CheckCircle size={14} className="text-white" />}
                                        </div>
                                        <div className="w-6 h-6 bg-rose-500 rounded-md flex items-center justify-center text-white"><Search size={14} /></div>
                                        <span className="font-bold text-slate-900 flex items-center gap-2">Werkzoeken.nl 
            {data.werkzoeken?.status === 'Online' && <span className="text-[9px] font-black bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full uppercase">Live</span>}
            {data.werkzoeken?.status === 'Aangevraagd' && <span className="text-[9px] font-black bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase flex items-center gap-1"><Clock size={10}/> Wachtend op activatie</span>}
            {data.werkzoeken?.activeLink && <a href={data.werkzoeken.activeLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-blue-500 hover:text-blue-700"><ExternalLink size={12} /></a>}
        </span>
                                    </div>
                                    <span className="font-bold text-slate-500">€{PRICING.werkzoeken.sponsored} /dag</span>
                                </div>
                                {data.werkzoeken?.selected && (
                                    <div className="pl-9 space-y-3 pt-2 border-t border-slate-200 mt-2" onClick={e => e.stopPropagation()}>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-medium text-slate-600">Looptijd tot:</span>
                                            <input type="date" value={data.werkzoeken.endDate || ''} onChange={(e) => handleChange('werkzoeken', { endDate: e.target.value })} className="border border-slate-200 rounded-md px-2 py-1 text-xs" />
                                            <span className="text-xs text-slate-400 font-bold ml-auto">{calculateDays(data.werkzoeken.endDate)} dagen</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ICT'er gezocht */}
                            {isIT && (
                                <div className={`p-4 rounded-xl border-2 transition-all flex flex-col gap-4 ${data.ictergezocht?.selected ? 'border-[#0ea5e9] bg-[#0ea5e9]/5' : 'border-slate-100'}`}>
                                    <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleBoard('ictergezocht')}>
                                        <div className="flex items-center gap-3">
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${data.ictergezocht?.selected ? 'border-[#0ea5e9] bg-[#0ea5e9]' : 'border-slate-300'}`}>
                                                {data.ictergezocht?.selected && <CheckCircle size={14} className="text-white" />}
                                            </div>
                                            <div className="w-6 h-6 bg-sky-500 rounded-md flex items-center justify-center text-white font-mono font-bold text-xs">{`</>`}</div>
                                            <span className="font-bold text-slate-900 flex items-center gap-2">ICT'er gezocht 
            {data.ictergezocht?.status === 'Online' && <span className="text-[9px] font-black bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full uppercase">Live</span>}
            {data.ictergezocht?.status === 'Aangevraagd' && <span className="text-[9px] font-black bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase flex items-center gap-1"><Clock size={10}/> Wachtend op activatie</span>}
            {data.ictergezocht?.activeLink && <a href={data.ictergezocht.activeLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-blue-500 hover:text-blue-700"><ExternalLink size={12} /></a>}
        </span>
                                        </div>
                                        <span className="font-bold text-slate-500">Vanaf €{PRICING.ictergezocht.basis}</span>
                                    </div>
                                    {data.ictergezocht?.selected && (
                                        <div className="pl-9 space-y-3 pt-2 border-t border-slate-200 mt-2" onClick={e => e.stopPropagation()}>
                                            <p className="text-xs text-slate-500 italic">Looptijd: 6 maanden</p>
                                            <div className="flex gap-2">
                                                <button onClick={() => handleChange('ictergezocht', { tier: 'basis' })} className={`flex-1 py-1.5 rounded-md text-xs font-bold border ${data.ictergezocht.tier === 'basis' ? 'bg-[#0ea5e9] text-white border-[#0ea5e9]' : 'bg-white text-slate-600 border-slate-200'}`}>Basis (€{PRICING.ictergezocht.basis})</button>
                                                <button onClick={() => handleChange('ictergezocht', { tier: 'premium' })} className={`flex-1 py-1.5 rounded-md text-xs font-bold border ${data.ictergezocht.tier === 'premium' ? 'bg-[#0ea5e9] text-white border-[#0ea5e9]' : 'bg-white text-slate-600 border-slate-200'}`}>Premium (€{PRICING.ictergezocht.premium})</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>

                        {/* Order Summary */}
                        <div className="mt-8 bg-slate-50 p-6 rounded-2xl border border-slate-100">
                            <div className="flex justify-between items-center mb-6">
                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                    <Calculator size={14} /> Totaalschatting
                                </h4>
                                <span className="text-2xl font-black text-slate-900 tracking-tight">€{calculateTotal().toLocaleString('nl-NL')}</span>
                            </div>

                            <button
                                className={`w-full py-4 rounded-xl font-black text-sm uppercase tracking-widest flex items-center justify-center gap-3 transition-all ${calculateTotal() > 0 && !isSaving ? 'bg-[#e51b23] text-white hover:bg-red-700 shadow-xl shadow-red-200 hover:scale-[1.02]' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                                disabled={calculateTotal() === 0 || isSaving}
                                onClick={handleSendRequest}
                            >
                                {isSaving ? (
                                    <svg className="animate-spin w-5 h-5 text-white" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"/><path fill="currentColor" d="M4 12a8 8 0 018-8v8z" className="opacity-75"/></svg>
                                ) : savedOk ? (
                                    <><CheckCircle size={18} /> Aanvraag Succesvol Verzonden!</>
                                ) : (
                                    <><Send size={18} /> Verstuur Aanvraag naar Recruiter</>
                                )}
                            </button>
                            <p className="text-center text-[10px] text-slate-400 mt-4 font-medium px-4">Aanvragen worden direct per e-mail naar het recruitment team gestuurd. Na activering ontvang je de live URL en status updates hierboven.</p>
                        </div>
                    </div>
                </div>

                {/* Preview Column */}
                <div className="lg:col-span-6">
                    <div className="sticky top-24">
                        <div className="bg-slate-100 h-[600px] rounded-[2.5rem] border border-slate-200 shadow-inner p-8 flex flex-col items-center justify-center overflow-y-auto w-full relative">
                            {!previewBoard ? (
                                <div className="text-center text-slate-400 flex flex-col items-center">
                                    <Eye size={48} className="mb-4 opacity-50" />
                                    <p className="font-medium text-lg">Selecteer een kanaal voor een live preview</p>
                                </div>
                            ) : (
                                <>
                                    <div className="absolute top-4 right-4 bg-white/80 backdrop-blur px-3 py-1 rounded-full text-[10px] font-black uppercase text-slate-500 shadow-sm border border-slate-200 z-10 flex items-center gap-2">
                                        Mockup Preview <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                    </div>

                                    <div className="w-full max-w-sm mx-auto animate-fadeIn mt-10">
                                        {/* Mockups */}
                                        {previewBoard === 'linkedin' && (
                                            <div className="bg-white rounded-lg shadow-sm w-full border border-slate-200 overflow-hidden text-left font-sans">
                                                <div className="h-16 bg-[#0077b5] p-4 flex items-center">
                                                    <span className="text-white font-bold text-lg">LinkedIn</span>
                                                </div>
                                                <div className="p-4">
                                                    <h3 className="text-lg font-bold text-slate-900 leading-tight mb-1">{vacancy.title}</h3>
                                                    <p className="text-sm font-medium text-slate-700 mb-2">{vacancy.client?.name}</p>
                                                    <p className="text-xs text-slate-500 pl-1 border-l-2 border-slate-300 mb-4">{vacancy.data?.location || 'Nederland'} ({vacancy.data?.workplaceType || 'Hybride'}) • Actief wervend</p>

                                                    <div className="flex gap-2 mb-4">
                                                        <span className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-600 font-medium">Volgens je profiel</span>
                                                        <span className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-600 font-medium flex items-center gap-1"><CheckCircle size={10} className="text-green-600" /> Easy Apply</span>
                                                    </div>

                                                    <p className="text-sm text-slate-700 line-clamp-4 leading-relaxed">
                                                        {vacancy.data?.finalVacancyContent?.substring(0, 150)}...
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {previewBoard === 'indeed' && (
                                            <div className="bg-white rounded-xl shadow-md p-5 w-full border border-slate-200 text-left font-sans">
                                                <h3 className="text-xl font-bold text-slate-900 mb-1">{vacancy.title}</h3>
                                                <p className="font-medium text-slate-700 mb-2 text-sm">{vacancy.client?.name} <span className="text-slate-500">• {vacancy.data?.location || 'Remote'}</span></p>
                                                <div className="bg-slate-100 rounded p-2 mb-4 mt-2">
                                                    <p className="text-sm font-bold text-slate-700">€{vacancy.data?.salary?.min || '3500'} - €{vacancy.data?.salary?.max || '5000'} per maand</p>
                                                </div>
                                                <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                                                    {vacancy.data?.finalVacancyContent?.substring(0, 150)}...
                                                </p>
                                                {data.indeed?.tier === 'premium' && (
                                                    <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-[#003A9B] bg-red- px-2 py-1 rounded">
                                                        <Sparkles size={12} /> Sponsored door werkgever
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {previewBoard === 'werkzoeken' && (
                                            <div className="bg-white p-5 w-full border-t-[6px] border-[#ff5a5f] shadow-lg rounded text-left">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <p className="text-xs text-slate-400 uppercase font-black mb-1">{vacancy.data?.category || 'Techniek / IT'}</p>
                                                        <h3 className="text-xl font-bold text-slate-900 mb-1">{vacancy.title}</h3>
                                                        <p className="text-sm font-medium text-[#ff5a5f]">{vacancy.client?.name}</p>
                                                    </div>
                                                    <div className="w-12 h-12 bg-slate-100 rounded object-cover flex items-center justify-center text-slate-300 font-bold text-xs shadow-inner">
                                                        LOGO
                                                    </div>
                                                </div>
                                                <p className="mt-4 text-sm text-slate-600 line-clamp-3">
                                                    {vacancy.data?.finalVacancyContent?.substring(0, 150)}...
                                                </p>
                                                <button className="mt-4 w-full bg-[#ff5a5f] text-white py-2 font-bold rounded">Solliciteer Direct</button>
                                            </div>
                                        )}

                                        {previewBoard === 'ictergezocht' && (
                                            <div className="w-full bg-[#F5F8FA] border border-slate-200 rounded-lg overflow-hidden text-left">
                                                <div className="bg-[#0ea5e9] p-3 text-white flex justify-between items-center">
                                                    <span className="font-bold">ICT'er gezocht</span>
                                                    {data.ictergezocht?.tier === 'premium' && (
                                                        <span className="text-[10px] bg-white text-[#0ea5e9] px-2 py-0.5 rounded-full font-black uppercase">Premium</span>
                                                    )}
                                                </div>
                                                <div className="p-4 bg-white">
                                                    <h3 className="text-lg font-bold text-slate-900 mb-1 hover:text-[#0ea5e9] cursor-pointer transition-colors">{vacancy.title}</h3>
                                                    <p className="text-sm text-slate-600 mb-3">{vacancy.client?.name} | {vacancy.data?.location || 'Nederland'}</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {['Ervaren', 'Hybride', 'Vast Contract'].map((tag, i) => (
                                                            <span key={i} className="text-[10px] text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200">{tag}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VacancyPremiumJobboards;
